import React from 'react';
import { NextImage } from '@sdf/base';
import { Styles } from '@t2c/shared-fe';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import {
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { CreditDialog } from '../../create-video/CreditDialog';
import { useTextToImagePage } from '../../../../t2c-fe/hooks/textToImage/useTextToImagePage';
import { TextToImageProccessingModal } from '../../text-to-image/TextToImageProccessingModal';
import { useMusicPage } from '../../../../t2c-fe/hooks/ai-music/useMusicPage';
import { MusicProccessingModal } from '../../ai-music/MusicProccessingModal';
import { AiMusicEnum, MUSIC_DURATION_LIMITS } from '../../../utils/constant';

export const AiMusicWorkBench: React.FC = () => {
  const {
    musicGenerationData,
    handleDataChange,
    handleDurationBlur,
    handleDurationKeyDown,
    influenceType,
    handleOutputTypeChange,
    handleMusicGenerate,
    isCreditDialogOpen,
    closeCreditDialog,
    isFetchingRequiredCredits,
    requiredCreditsData,
    handleConfirmGeneration,
    musicGenerationLoading,
    isProccessingModalOpen,
    closeProccessingModal,
    handleUpgrade,
    musicGenerationError,
    isEnhancing,
    handlePromptEnhance,
  } = useMusicPage({ refetchmusic: () => {} });

  const maxCharLimit = 450;

  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid container gap={3}>
      <Grid item xs={12}>
        <Typography sx={styles.heading}>
          Let AI create the perfect soundtrack for your mood and style!{' '}
        </Typography>
        <Box sx={{ position: 'relative' }}>
          <TextField
            placeholder="E.g., Create an energetic 2-minute music track in an electronic style that conveys excitement and motivation. Think of the vibe you'd get from a fast-paced video game or a high-energy workout session. The mood should be upbeat and inspiring, with a strong beat and dynamic transitions."
            size="medium"
            type="text"
            name="prompt"
            multiline
            inputProps={{ maxLength: maxCharLimit }}
            rows={isExtraSmall ? 2 : 9}
            sx={styles.textField}
            onChange={(e) => handleDataChange('prompt', e.target.value?.slice(0, maxCharLimit))}
            value={musicGenerationData.prompt}
            fullWidth
          />
          <Tooltip title="Enhance prompt with AI" placement="top">
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 4,
                zIndex: 10,
              }}
              disabled={!musicGenerationData.prompt.trim() || isEnhancing}
              onClick={handlePromptEnhance}
            >
              {isEnhancing ? (
                <CircularProgress size={20} />
              ) : (
                <NextImage
                  src={'/assets/icons/create-video_default.svg'}
                  alt=""
                  width={24}
                  height={24}
                />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ color: (theme) => theme.palette.text.placeholder, pl: '2px', pt: '2px' }}
          >
            {musicGenerationData?.prompt?.length || 0}/{maxCharLimit} characters
          </Typography>
        </Box>
      </Grid>
      <Box display={'flex'} gap={2} width={'100%'} flexDirection={isExtraSmall ? 'column' : 'row'}>
        <Box flex={1}>
          <Typography sx={styles.label}>Enter Name</Typography>
          <TextField
            placeholder="Type name here"
            size="medium"
            type="text"
            name="name"
            sx={styles.textField}
            onChange={(e) => handleDataChange('name', e.target.value)}
            value={musicGenerationData.name}
            fullWidth
          />
        </Box>
        <Box flex={1}>
          <Typography sx={styles.label}>Duration (in seconds)</Typography>
          <TextField
            size="medium"
            type="number"
            name="duration"
            sx={styles.textField}
            onChange={(e) => {
              handleDataChange('duration', e.target.value);
            }}
            onBlur={handleDurationBlur}
            onKeyDown={handleDurationKeyDown}
            inputProps={{
              min: MUSIC_DURATION_LIMITS.MIN,
              max: MUSIC_DURATION_LIMITS.MAX,
              step: 1,
              inputMode: 'numeric',
            }}
            value={musicGenerationData.duration}
            fullWidth
          />
        </Box>
      </Box>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Stack>
              <Typography sx={styles.label}>Influence</Typography>
              <Select
                labelId="influence-select-label"
                value={influenceType}
                onChange={handleOutputTypeChange}
                sx={{
                  width: '100%',
                  backgroundColor: (theme) => theme.palette.background.secondary,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: (theme) => theme.palette.background.secondary,
                      '& .MuiMenuItem-root': {
                        backgroundColor: (theme) => theme.palette.background.secondary,
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.background.activeCard,
                        },
                      },
                    },
                  },
                }}
              >
                {Object.values(AiMusicEnum).map((output) => (
                  <MenuItem key={output} value={output}>
                    {output}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{
              alignContent: 'flex-end',
            }}
          >
            <Button
              variant="contained"
              fullWidth
              sx={styles.createBtn}
              endIcon={<NextImage src={'/assets/icons/Ai.svg'} alt="" width={24} height={24} />}
              onClick={handleMusicGenerate}
              disabled={isFetchingRequiredCredits || musicGenerationLoading}
            >
              Create
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <CreditDialog
        isOpen={isCreditDialogOpen}
        onClose={closeCreditDialog}
        isFetchingCreditDetail={isFetchingRequiredCredits}
        onConfirm={handleConfirmGeneration}
        creditDetail={{
          availableCredit: requiredCreditsData?.availableCredits,
          creditNeeded: requiredCreditsData?.requiredCredits,
          creditsInUse: requiredCreditsData?.creditsInUse,
        }}
        isLoading={musicGenerationLoading}
        onUpgrade={handleUpgrade}
        enoughCreditConfirmBtnTitle={'Generate'}
        enoughCreditModalTitle="Confirm Generation"
        upgradeButtonTitle="Upgrade"
      />

      {isProccessingModalOpen && (
        <MusicProccessingModal
          handleCloseDialog={closeProccessingModal}
          shouldOpenDialog={isProccessingModalOpen}
          error={musicGenerationError}
        />
      )}
    </Grid>
  );
};

const styles: Styles = {
  textField: {
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '6px',
      borderColor: (theme) => theme.palette.background.ternary,
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.activeBorder,
      },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: (theme) => theme.palette.text.placeholder,
    },
  },
  heading: {
    fontSize: 14,
    fontWeight: 500,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
    marginTop: '5px',
  },
  label: {
    fontSize: 14,
    fontWeight: 400,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
  },
  uploadText: {
    textAlign: 'center',
    color: (theme) => theme.palette.primary.main,
    fontWeight: 600,
  },
  uploadContainer: {
    height: 100,
    width: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    marginTop: '10px',
  },
  createBtn: {
    py: 3,
    height: '56px',
    fontSize: 16,
    color: (theme) => theme.palette.common.white,
    boxShadow: 'none',
  },
  uploading: {
    position: 'relative',
    borderRadius: '10px',
    width: '210px',
  },
  uploader: {
    height: 100,
    width: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    textAlign: 'center',
    borderStyle: 'dashed',
    borderRadius: '10px',
    flexDirection: 'column',
    borderColor: (theme) => theme.palette.text.coloredText,
    backgroundColor: (theme) => theme.palette.background.card,
    cursor: 'pointer',
  },
  uploaderText: {
    color: (theme) => theme.palette.text.coloredText,
    fontSize: 18,
    fontWeight: 500,
  },
};
